/**
 * Images that sit INSIDE the Builder's reply text (FRONT-3163), at the spot
 * where the person dropped or pasted them.
 *
 * In the editor an image is ordinary Markdown, `![name](url)`. While it
 * uploads, the url is a placeholder naming the upload (`upload:<localId>`);
 * once the file exists it becomes the file's own address in Eddy
 * (`/api/files/<id>/raw`), which the transcript can show. The Builder's
 * server rewrites that address to the file's path in the project before the
 * agent reads the message (`rewriteInlineUploadRefs` in `src/coding/uploads.ts`),
 * so the agent sees each image's path exactly where it belongs in the text.
 *
 * Pure string functions, so every rule here is tested without a browser.
 */

const UPLOAD_SCHEME = 'upload:';

/** The placeholder address of an image that is still uploading. */
export function uploadUrl(localId: string): string {
  return `${UPLOAD_SCHEME}${localId}`;
}

/** The address Eddy serves an uploaded file's bytes from. */
export function fileRawUrl(fileId: string): string {
  return `/api/files/${encodeURIComponent(fileId)}/raw`;
}

/**
 * `![name](url)` for an image. Square brackets in a file name would end the
 * alt text early, so they are dropped; a space or parenthesis in the address
 * would end the link, so the address is percent-encoded there.
 */
export function imageMarkdown(name: string, url: string): string {
  const alt = name.replace(/[[\]\\]/g, '').trim() || 'image';
  const safeUrl = url.replace(/[ ()]/g, (c) => ({ ' ': '%20', '(': '%28', ')': '%29' })[c] as string);
  return `![${alt}](${safeUrl})`;
}

/**
 * `[Attached: name](url)` for any other file (eddy, 2026-10-27): a visible
 * line in the text saying the file came with the message, one per file. It is
 * a link to the file, so it travels exactly like an inline image — the server
 * rewrites the address to the file's path in the project, and deleting the
 * line takes the file off the message.
 */
export function fileNoteMarkdown(name: string, url: string): string {
  const label = name.replace(/[[\]\\]/g, '').trim() || 'file';
  return imageMarkdown(label, url).replace(/^!\[[^\]]*\]/, `[Attached: ${label}]`);
}

/** What an upload placed in the text looks like: a picture for an image, the attached-file line for anything else. */
export function uploadMarkdown(file: Pick<File, 'name' | 'type'>, url: string): string {
  return isImageFile(file) ? imageMarkdown(file.name, url) : fileNoteMarkdown(file.name, url);
}

/** Where `![…](upload:<localId>)` (or a file's `[…](upload:<localId>)`) sits in `text`, or null once it is gone (the person deleted it). */
export function findUploadImage(text: string, localId: string): { from: number; to: number; urlFrom: number; urlTo: number } | null {
  const url = uploadUrl(localId);
  const re = /!?\[[^\]\n]*\]\(([^)\s]+)\)/g;
  for (let m = re.exec(text); m; m = re.exec(text)) {
    if (m[1] === url) {
      const urlFrom = m.index + m[0].length - 1 - url.length;
      return { from: m.index, to: m.index + m[0].length, urlFrom, urlTo: urlFrom + url.length };
    }
  }
  return null;
}

/** The ids of every uploaded file the text still shows inline — as a picture or as an attached-file line. */
export function inlineFileIds(text: string): Set<string> {
  const ids = new Set<string>();
  const re = /!?\[[^\]\n]*\]\(\/api\/files\/([^/)\s]+)\/raw\)/g;
  for (let m = re.exec(text); m; m = re.exec(text)) ids.add(decodeURIComponent(m[1]));
  return ids;
}

/**
 * The text an image drop inserts at `pos`: on its own line, so it never glues
 * to a word. It always ends in a line break, even at the end of the text: the
 * cursor lands after the insertion, and the live preview shows the line the
 * cursor is on as raw syntax — without the break, a dropped image showed as
 * its path instead of the picture (FRONT-3138).
 */
export function imageInsertion(doc: string, pos: number, markdown: string): string {
  const before = pos > 0 && doc[pos - 1] !== '\n' ? '\n' : '';
  const after = doc[pos] !== '\n' ? '\n' : '';
  return `${before}${markdown}${after}`;
}

export function isImageFile(file: Pick<File, 'type'>): boolean {
  return file.type.startsWith('image/');
}

/**
 * The attachments a send carries: everything ready, except an inline image the
 * person has since deleted from the text — removing the picture removes the file.
 */
export function keepShownInline<T extends { inline?: boolean; fileId: string }>(ready: readonly T[], text: string): T[] {
  const shown = inlineFileIds(text);
  return ready.filter((a) => !a.inline || shown.has(a.fileId));
}
